<template>
    <div class="flex flex-column md:p-4">
        <div class="flex flex-wrap justify-content-between gap-2 py-2">
            <div class="flex justify-content-start align-items-center gap-2">
                <div v-if="initialLoadingComplete" class="flex justify-center md:justify-start align-items-center">
                    <img :src="profileImageUrl" alt="Profile Image" class="profile-image" />
                </div>
                <div class="flex flex-column justify-content-start gap-2">
                    <span class="text-left Paragraph-Medium">{{ request.studentId.username }}</span>
                    <span class="Caption">{{ cleanupLables(request.grade) }}</span>
                    <span class="Caption">{{ classModelText }}</span>
                </div>
            </div>
        </div>

        <Divider class="w-full" />

        <div class="flex flex-column justify-content-center align-items-center w-full h-full">
            <span class="H6-Medium py-4">Schedule a Class</span>

            <form @submit.prevent="submitRequest" class="form w-full">
                <div class="grid w-full align-items-start justify-content-center">

                    <!-- Left Column -->
                    <div class="col-12 md:col-6">
                        <FieldWrapper label="Student" :required="true">
                            <Select v-model="form.student" :options="students" optionLabel="name"
                                placeholder="Select your student" class="w-full" variant="filled" size="small"
                                :invalid="!!errors.student" />
                            <small class="error-text-block">{{ errors.student }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Subject" :required="true">
                            <Select v-model="form.subject" :options="subjects" optionLabel="name"
                                placeholder="Select your subject" class="w-full" variant="filled" size="small"
                                :invalid="!!errors.subject" />
                            <small class="error-text-block">{{ errors.subject }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Class Mode" :required="true">
                            <Select v-model="form.classModel" :options="classModels" optionLabel="name"
                                placeholder="Select class model" class="w-full" variant="filled" size="small"
                                :invalid="!!errors.classModel" />
                            <small class="error-text-block">{{ errors.classModel }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Language" :required="true">
                            <Select v-model="form.language" :options="languages" optionLabel="name"
                                placeholder="Select language" class="w-full" variant="filled" size="small"
                                :invalid="!!errors.language" />
                            <small class="error-text-block">{{ errors.language }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Class Location" :required="isPhysicalClass">
                            <Select v-model="form.location" :options="locations" optionLabel="name"
                                placeholder="Select class location" class="w-full" variant="filled" size="small"
                                :disabled="!isPhysicalClass" :invalid="!!errors.location" />
                            <small class="error-text-block">{{ errors.location }}</small>
                        </FieldWrapper>
                    </div>

                    <!-- Right Column -->
                    <div class="col-12 md:col-6">
                        <FieldWrapper label="Class Type" :required="true">
                            <Select v-model="form.classType" :options="classTypes" optionLabel="name"
                                placeholder="Select class type" class="w-full" variant="filled" size="small"
                                :invalid="!!errors.classType" />
                            <small class="error-text-block">{{ errors.classType }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Location Address" :required="isPhysicalClass">
                            <InputText id="address" type="text" placeholder="Enter address here"
                                v-model="form.locationAddress" class="w-full" variant="filled" size="small"
                                :disabled="!isPhysicalClass || isTutorsPlace" :invalid="!!errors.locationAddress" />
                            <small class="error-text-block">{{ errors.locationAddress }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Date" :required="true">
                            <DatePicker v-model="form.date" dateFormat="dd/mm/yy" showIcon iconDisplay="input"
                                class="w-full" variant="filled" size="small" :invalid="!!errors.date" />
                            <small class="error-text-block">{{ errors.date }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Time Slot" :required="true">
                            <div class="flex flex-column sm:flex-row gap-2 w-full">
                                <DatePicker v-model="form.startTime" timeOnly hourFormat="12" showIcon
                                    iconDisplay="input" variant="filled" placeholder="Start Time" size="small"
                                    class="w-full" :invalid="!!errors.startTime" @date-select="validateTimeSlot"/>

                                <DatePicker v-model="form.endTime" :minDate="form.startTime" timeOnly hourFormat="12" showIcon iconDisplay="input"
                                    variant="filled" placeholder="End Time" size="small" class="w-full"
                                    :invalid="!!errors.endTime" @date-select="validateTimeSlot"/>
                            </div>
                            <small class="error-text-block">{{ errors.startTime ? 'Start time is required' : (errors.endTime || '') }}</small>
                        </FieldWrapper>

                        <FieldWrapper label="Other Students" :required="isGroupClass">
                            <InputText id="other_students" type="text"
                                placeholder="Enter student emails here (comma-separated)" v-model="form.otherStudents"
                                class="w-full" variant="filled" size="small" :disabled="!isGroupClass"
                                :invalid="!!errors.otherStudents" />
                            <small class="error-text-block">{{ errors.otherStudents }}</small>
                        </FieldWrapper>
                    </div>
                    
                    <div class="col-12 p-3">
                        <div class="flex align-items-center gap-3 mb-1">
                            <Checkbox v-model="form.hasRepeat" class="teal-checkbox" binary inputId="repeat" />
                            <label for="repeat" class="Label text-left">Repeat Classes</label>
                        </div>
                        <small class="error-text-block mb-3"
                            :style="!(form.hasRepeat && form.endsAfterOccurrences <= 1) ? 'color: var(--p-text-muted-color, #64748b);' : ''">
                            {{
                                form.hasRepeat && form.endsAfterOccurrences <= 1
                                    ? 'Occurrences set to 1, this will only schedule a single session. Increase occurrences to set up a recurring schedule.'
                                    : 'Use this only for recurring classes. For a single session, leave this unchecked.'
                            }}
                        </small>

                        <div v-if="form.hasRepeat"
                            class="flex flex-column justify-content-start align-self-start gap-4 mb-5">
                            <div class="flex flex-nowrap align-items-center gap-2">
                                <span class="placeholder-text whitespace-nowrap">Repeat every</span>
                                <InputNumber v-model="form.repeatFrequency" inputId="repeatFrequency" variant="filled"
                                    size="small" :min="1" :max="52" showButtons buttonLayout="stacked"
                                    class="text-center" :pt="{
                                        root: { class: 'p-inputnumber' },
                                        input: { class: 'text-center border-gray-300' }
                                    }" />
                                <span class="placeholder-text pl-2 whitespace-nowrap">Week(s)</span>
                            </div>
                            <div class="flex flex-column gap-1">
                                <div class="flex align-items-center gap-4 w-full">
                                    <span class="whitespace-nowrap placeholder-text pt-1">Repeat on</span>
                                    <div class="flex gap-2 flex-wrap overflow-auto p-1">
                                        <Button :label="day.label" variant="text" raised v-for="day in days"
                                            :key="day.code" @click="toggleDay(day.code)" :class="[
                                                'border-1 teal-button-small duration-150',
                                                form.repeatDays.includes(day.code)
                                                    ? 'bg-teal-500 border-teal-500 text-white'
                                                    : 'bg-white border-gray-600 text-gray-600 hover:bg-gray-100'
                                            ]" />
                                    </div>
                                </div>
                                <small class="error-text-block">{{ errors.repeatDays }}</small>
                            </div>
                            <div class="flex flex-column gap-1">
                                <div class="flex flex-nowrap align-items-center gap-2">
                                    <span class="placeholder-text pr-4 whitespace-nowrap">Ends after</span>
                                    <InputNumber v-model="form.endsAfterOccurrences" inputId="endsAfterOccurrences"
                                        variant="filled" size="small" :min="1" :max="100" showButtons
                                        buttonLayout="stacked" class="text-center"
                                        :invalid="!!errors.repeatOccurrences" :pt="{
                                            root: { class: 'p-inputnumber' },
                                            input: { class: 'text-center border-gray-300' }
                                        }" />
                                    <span class="placeholder-text pl-2 whitespace-nowrap">Occurrence(s)</span>
                                </div>
                                <small class="error-text-block">{{ errors.repeatOccurrences }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 flex flex-column gap-3 pt-4">
                        <label for="areasToImprove" class="Label align-self-start">Specific areas want to improve</label>
                        <IconField class="w-full w-full flex align-items-start">
                            <InputIcon class="pi pi-file" />
                            <Textarea v-model="form.areasToImprove" variant="filled" autoResize rows="4" class="w-full" />
                        </IconField>
                    </div>
                    
                    <div class="col-12 flex flex-column gap-3 pt-4">
                        <label for="note" class="Label align-self-start">Note</label>
                        <IconField class="w-full w-full flex align-items-start">
                            <InputIcon class="pi pi-file" />
                            <Textarea v-model="form.note" variant="filled" autoResize rows="4" class="w-full" />
                        </IconField>
                    </div>

                    <div v-if="localConflicts.items?.length > 0" class="col-12">
                        <div class="conflict-box">
                            <div class="flex align-items-center justify-content-between gap-2 mb-2">
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-exclamation-triangle conflict-icon text-xl"></i>
                                    <span class="Paragraph-Medium conflict-title">
                                        {{ isTutorConflict ? 'Your Schedule Has a Conflict' : 'Student Already Has a Class Scheduled' }}
                                    </span>
                                </div>
                                <Button icon="pi pi-times" text rounded size="small" class="conflict-close p-0" @click="localConflicts = { message: '', items: [] }" />
                            </div>
                            <p class="Label mb-3">
                                {{ isTutorConflict
                                    ? 'You already have a class scheduled during this time. Please choose a different time slot.'
                                    : 'The student already has a class scheduled during this time. Please choose a different time slot.' }}
                            </p>
                            <div v-for="(conflict, i) in localConflicts.items" :key="i" class="conflict-row">
                                <div class="flex flex-column gap-1">
                                    <span class="Label"><span class="font-semibold">Date:</span> {{ conflict.date }}</span>
                                    <span class="Label"><span class="font-semibold">{{ isTutorConflict ? 'Your existing class:' : 'Student\'s existing class:' }}</span>
                                        {{ conflict.existingStart }} – {{ conflict.existingEnd }}
                                        <template v-if="isTutorConflict"> with {{ conflict.existingStudent }} ({{ cleanupLables(conflict.existingSubject) }})</template>
                                    </span>
                                    <span class="Label"><span class="font-semibold">Requested time:</span> {{ conflict.newStart }} – {{ conflict.newEnd }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 flex justify-content-end">
                        <Button label="Schedule a class" class="teal-button mt-6" type="submit" />
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref, watch, computed, reactive, defineEmits, nextTick, onMounted } from "vue";
import { publicProfile } from "@/api/tutor";
import { usePlatformLimitsStore } from "@/stores/platformLimitsStore";
import Button from "primevue/button";
import Divider from "primevue/divider";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import DatePicker from 'primevue/datepicker';
import Textarea from 'primevue/textarea';
import FieldWrapper from '@/components/FieldWrapper.vue';
import { notifyWarn, notifyError } from "@/utils/notification";
import { InputNumber } from "primevue";
import { Checkbox } from "primevue";
import { cleanupLables, formatToDisplayTime, toLocalISOString } from "@/utils/cleanups";
import { parseOtherStudentEmails, unregisteredEmailsMessage, validateOtherStudentEmails } from "@/utils/otherStudents";
import { generateSignedUrl } from "@/api/storage";
import blankProfile from '@/assets/blank-profile.png';

// --- CONSTANTS ---
const CLASS_LOCATIONS = [
    { name: "Tutor’s place", code: "tutors_place" },
    { name: "Student’s place", code: "students_place" },
];
const days = [
    { label: 'M', code: 'monday' },
    { label: 'Tu', code: 'tuesday' },
    { label: 'W', code: 'wednesday' },
    { label: 'Th', code: 'thursday' },
    { label: 'F', code: 'friday' },
    { label: 'Sa', code: 'saturday' },
    { label: 'S', code: 'sunday' },
];

// --- PROPS & EMITS ---
const props = defineProps({
    request: { type: Object, required: true },
    enrolledStudents: { type: Array, required: false, default: null },
    conflicts: { type: Object, required: false, default: () => ({ message: '', items: [] }) },
    unregisteredStudents: { type: Array, required: false, default: () => [] },
});
const emit = defineEmits(['submit-request']);

const platformLimitsStore = usePlatformLimitsStore();
onMounted(() => { platformLimitsStore.fetchIfNeeded(); });

// --- REACTIVE STATE ---
const students = ref([]);
const classTypes = ref([]);
const subjects = ref([]);
const locations = ref(CLASS_LOCATIONS);
const classModels = ref([]);
const languages = ref([]);
const allTeachingEntries = ref([]);

const initialLoadingComplete = ref(false);
const profileImageUrl = ref('');
const errors = ref({});
const tutorAddress = ref('');
const localConflicts = ref({ message: '', items: [] });
const isTutorConflict = computed(() => localConflicts.value.message?.toLowerCase().includes('tutor'));

watch(() => props.conflicts, (val) => {
    localConflicts.value = val?.items?.length ? val : { message: '', items: [] };
}, { immediate: true });

const form = reactive({
    student: null,
    classType: null,
    subject: null,
    location: null,
    locationAddress: null,
    date: null,
    startTime: null,
    endTime: null,
    areasToImprove: null,
    note: null,
    classModel: null,
    otherStudents: null,
    hasRepeat: false,
    repeatEveryWeekCount: 0,
    repeatDays: [],
    repeatFrequency: 1,
    endsAfterOccurrences: 1,
    language: null
});

// --- COMPUTED ---
const isPhysicalClass = computed(() => form.classType?.value === 'physical');
const isGroupClass = computed(() => form.classModel?.code === 'group_class');
const isTutorsPlace = computed(() => form.location?.code === 'tutors_place');

const classModelText = computed(() => {
    if (!props.request.classModel) return '';
    if (props.request.classModel === 'individual_class') return 'Individual Class';
    if (props.request.classModel === 'group_class') return 'Group Class';
    return cleanupLables(props.request.classModel);
});

// --- HELPERS ---

const formatPrice = (amount) => {
    if (amount == null) return '0.00';
    return new Intl.NumberFormat('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const buildClassModels = (subjectCode, gradeCode) => {
    const entry = allTeachingEntries.value.find(e => e.grade === gradeCode && e.subject === subjectCode);
    const models = [];
    if (entry) {
        if (entry.individualClass?.available) {
            models.push({ name: `One-on-One Class (LKR. ${formatPrice(entry.individualClass.charge)})`, code: 'individual_class', charge: entry.individualClass.charge });
        }
        if (entry.groupClass?.available) {
            models.push({ name: `Group Class (LKR. ${formatPrice(entry.groupClass.charge)})`, code: 'group_class', charge: entry.groupClass.charge });
        }
    }
    classModels.value = models;
};

// --- METHODS ---

const validateForm = () => {
    errors.value = {};

    if (!form.student) errors.value.student = "Student is required";
    if (!form.subject) errors.value.subject = "Subject is required";
    if (!form.classModel) errors.value.classModel = "Class mode is required";
    if (!form.language) errors.value.language = "Language is required";
    if (!form.classType) errors.value.classType = "Class type is required";
    if (!form.date) errors.value.date = "Date is required";
    if (!form.startTime) errors.value.startTime = "Start time is required";
    if (!form.endTime) {
        errors.value.endTime = "Valid end time is required";
    } else if (form.startTime) {
        const durationMin = (form.endTime.getTime() - form.startTime.getTime()) / 60000;
        if (durationMin < platformLimitsStore.minSessionDurationMinutes) {
            errors.value.endTime = `Session duration must be at least ${platformLimitsStore.minSessionDurationMinutes} minutes (got ${Math.round(durationMin)} min)`;
        }
    }

    if (isPhysicalClass.value) {
        if (!form.location) errors.value.location = "Location is required";
        // If Physical, Address is always required (either auto-filled or typed)
        if (!form.locationAddress) {
            errors.value.locationAddress = "Address is required";
        }
    }

    if (isGroupClass.value) {
        const otherStudentsError = validateOtherStudentEmails(form.otherStudents);
        if (otherStudentsError) errors.value.otherStudents = otherStudentsError;
    }

    if (form.hasRepeat && form.repeatDays.length === 0) {
        errors.value.repeatDays = "Select at least one day to repeat on";
    }
    if (form.hasRepeat && form.endsAfterOccurrences <= 1) {
        errors.value.repeatOccurrences = "Occurrences must be greater than 1 for a recurring schedule";
    }

    return Object.keys(errors.value).length === 0;
};

const fetchTutorProfile = async (tutorId) => {
    try {
        const response = await publicProfile(tutorId);
        const profile = response.data.data;

        tutorAddress.value = profile.address || profile.city || "";

        const types = [];
        if (profile.classMode === "online" || profile.classMode === "both") {
            types.push({ name: "Online", value: "online" });
        }
        if (profile.classMode === "physical" || profile.classMode === "both") {
            types.push({ name: "Physical", value: "physical" });
        }
        classTypes.value = types;

        // Store flat entries for subject-level class model lookup
        allTeachingEntries.value = profile.teachingLevels || [];

        // Filter subjects for the requested grade (grade is a plain string)
        const requestGrade = props.request.grade;
        const gradeEntries = allTeachingEntries.value.filter(e => e.grade === requestGrade);
        subjects.value = gradeEntries.map(e => ({ name: cleanupLables(e.subject), code: e.subject }));

        if (profile.teachingLanguage) {
            languages.value = profile.teachingLanguage.map(lang => ({
                name: cleanupLables(lang),
                code: lang
            }));
        }

        // Pre-select from request values
        const initialLocation = locations.value.find(l => l.code === props.request.classLocation);
        if (initialLocation) form.location = initialLocation;

        const initialType = classTypes.value.find(t => t.value === props.request.classType);
        if (initialType) form.classType = initialType;

        const initialLang = languages.value.find(l => l.code === props.request.language);
        if (initialLang) form.language = initialLang;

        const initialSubject = subjects.value.find(s => s.code === props.request.subject);
        if (initialSubject) {
            form.subject = initialSubject;
            // Wait for subject watcher to rebuild classModels, then pre-select the model
            await nextTick();
            const initialModel = classModels.value.find(m => m.code === props.request.classModel);
            if (initialModel) form.classModel = initialModel;
        }

    } catch (error) {
        notifyWarn("Hold on...", "We're having trouble fetching tutor profile data. Please try refresh the page again!");
    }
};

const toggleDay = (dayCode) => {
    const index = form.repeatDays.indexOf(dayCode);
    if (index > -1) {
        form.repeatDays.splice(index, 1);
    } else {
        form.repeatDays.push(dayCode);
        errors.value.repeatDays = null;
    }
};

const combineDateAndTime = (dateSource, timeSource) => {
        if (!dateSource || !timeSource) return null;
        const d = new Date(dateSource);
        const t = new Date(timeSource);
        t.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
        return toLocalISOString(t);
};

const validateTimeSlot = () => {
    if (form.startTime && form.endTime) {
        if (form.endTime.getTime() <= form.startTime.getTime()) {
            errors.value.endTime = "End time must be after start time";
            form.endTime = null;
            return false;
        }
        const durationMin = (form.endTime.getTime() - form.startTime.getTime()) / 60000;
        if (durationMin < platformLimitsStore.minSessionDurationMinutes) {
            errors.value.endTime = `Session duration must be at least ${platformLimitsStore.minSessionDurationMinutes} minutes (got ${Math.round(durationMin)} min)`;
            form.endTime = null;
            return false;
        }
    }
    errors.value.endTime = null;
    return true;
};

const submitRequest = () => {
    if (!validateForm()) {
        notifyError("Error", "Please fill all required fields.");
        return;
    }

    const payload = {
        classLocation: form.location ? form.location.code : null,
        locationAddress: form.locationAddress ? form.locationAddress : null,
        otherStudents: parseOtherStudentEmails(form.otherStudents),
        areasToImprove: form.areasToImprove ? form.areasToImprove : null,
        note: form.note ? form.note : null,
        grade: props.request.grade ? props.request.grade : null,
        language: form.language.code ? form.language.code : null,
        date: form.date ? toLocalISOString(form.date) : null,
        start: combineDateAndTime(form.date, form.startTime),
        end: combineDateAndTime(form.date, form.endTime),
        studentId: form.student.id ? form.student.id : null,
        classType: form.classType.value ? form.classType.value : null,
        classModel: form.classModel.code ? form.classModel.code : null,
        subject: form.subject.code ? form.subject.code : null,
        location: form.location ? form.location.code : null,
    };
    if (form.hasRepeat) {
        payload.recurrence = {
            enabled: form.hasRepeat,
            repeatWeeks: form.repeatFrequency,
            repeatOn: form.repeatDays,
            totalOccurrences: form.endsAfterOccurrences
        }
    }
    emit('submit-request', payload);
};


// --- WATCHERS ---
watch(() => props.request, (newRequest) => {
    if (newRequest && newRequest._id) {
        students.value = [{ name: newRequest.studentId.username, id: newRequest.studentId._id }];

        form.student = students.value[0];
        form.locationAddress = newRequest.locationAddress || "";
        form.date = newRequest.date ? new Date(newRequest.date) : null;
        form.startTime = newRequest.start ? formatToDisplayTime(newRequest.start) : null;
        form.endTime = newRequest.end ? formatToDisplayTime(newRequest.end) : null;
        form.areasToImprove = newRequest.areasToImprove || "";
        form.note = newRequest.note || "";
        form.otherStudents = newRequest.otherStudents ? (Array.isArray(newRequest.otherStudents) ? newRequest.otherStudents.join(', ') : newRequest.otherStudents) : "";

        fetchTutorProfile(newRequest.tutorId).then(async () => {
            if (newRequest.studentPhotoURL) {
                profileImageUrl.value = await generateSignedUrl(newRequest.studentPhotoURL);
            } else {
                profileImageUrl.value = blankProfile;
            }
            initialLoadingComplete.value = true;
        });
    } else if (newRequest) {
        form.student = { name: newRequest.studentId.username, id: newRequest.studentId._id };
    }
}, { immediate: true });

watch(() => props.enrolledStudents, (newData) => {
    if (newData) {
        students.value = newData.map(student => ({ name: student.studentName, id: student.studentId }));
    }
}, { immediate: true });

watch(isPhysicalClass, (newIsPhysical) => {
    if (newIsPhysical) errors.value.classType = null;
    if (!newIsPhysical) {
        form.location = null;
        form.locationAddress = null;
    }
});

// Handle auto-filling based on Location selection
watch(() => form.location, (newLocation) => {
    if (newLocation?.code === 'tutors_place') {
        form.locationAddress = tutorAddress.value; 
        errors.value.locationAddress = null; 
    } else if (newLocation?.code === 'students_place') {
        form.locationAddress = ''; 
    }
    if (newLocation) errors.value.location = null;
});

watch(isGroupClass, (newIsGroup) => {
    if (newIsGroup) errors.value.classModel = null;
    if (!newIsGroup) {
        form.otherStudents = null;
    }
});

watch(() => form.hasRepeat, (newVal) => {
    if (!newVal) {
        form.repeatFrequency = 1;
        form.repeatDays = ['Tu'];
        form.endsAfterOccurrences = 10;
    }
});


// Clear errors on input change (UX improvement)
watch(() => form.student, (val) => { if (val) errors.value.student = null; });
watch(() => form.subject, (newSubject) => {
    if (newSubject) errors.value.subject = null;
    form.classModel = null;
    classModels.value = [];
    if (!newSubject?.code) return;
    buildClassModels(newSubject.code, props.request.grade);
});
watch(() => form.classModel, (val) => { if (val) errors.value.classModel = null; });
watch(() => form.language, (val) => { if (val) errors.value.language = null; });
watch(() => form.classType, (val) => { if (val) errors.value.classType = null; });
watch(() => form.locationAddress, (val) => { if (val) errors.value.locationAddress = null; });
watch(() => form.date, (val) => { if (val) errors.value.date = null; });
watch(() => form.startTime, (val) => { if (val) { errors.value.startTime = null; localConflicts.value = { message: '', items: [] }; } });
watch(() => form.endTime, (val) => { if (val) { errors.value.endTime = null; localConflicts.value = { message: '', items: [] }; } });
watch(() => form.endsAfterOccurrences, (val) => { if (val > 1) errors.value.repeatOccurrences = null; });
watch(() => form.otherStudents, (val) => { if (val) errors.value.otherStudents = null; });

// Emails the API rejected as not registered on the platform
watch(() => props.unregisteredStudents, (emails) => {
    if (emails?.length) errors.value.otherStudents = unregisteredEmailsMessage(emails);
});

</script>

<style scoped>
:deep(.p-inputnumber-button-group) {
    display: flex;
    flex-direction: column;
    height: 100%;
}

:deep(.p-inputnumber-button-group) button {
    flex: 1;
    padding: 0 0.4rem !important;
}

.teal-button-small {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 12px !important;
    color: var(--orange-500) !important;
}

.error-text-block {
    color: #dc2626;
    font-size: 0.75rem;
    display: block;
    margin-top: 0.05rem;
    min-height: 0.85rem;
    line-height: 0.85rem;
    overflow-wrap: anywhere;
}

:deep(.p-datepicker) {
    width: 100%;
}

:deep(.p-datepicker .p-inputtext) {
    width: 100%;
}

.conflict-box {
    background: #fdf8ec;
    border: 1px solid #e9a30c;
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
    margin-top: 0.5rem;
}

.conflict-title {
    color: #e9a30c;
}

.conflict-icon {
    color: #e9a30c;
}

.conflict-close {
    color: #e9a30c !important;
}

.conflict-row {
    border-top: 1px dashed #e9a30c80;
    padding-top: 0.75rem;
    margin-top: 0.75rem;
}

.conflict-row:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
}
</style>